import { IconButton, Stack, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import { type FC, useRef, useState } from "react";
import { ChatAttachmentDrawer } from "web_myplann/Chat/Components/Composer/ChatAttachmentDrawer";
import { ChatComposerInput } from "web_myplann/Chat/Components/Composer/ChatComposerInput";
import { ChatComposerSendButton } from "web_myplann/Chat/Components/Composer/ChatComposerSendButton";

type Props = {
	value: string;
	onChange: (value: string) => void;
	onSend: () => void;
	isFirstMessage: boolean;
	canAttach: boolean;
	onPickPhoto: () => void;
	onPickGallery: () => void;
	onPickFile: () => void;
};

const ICON_BUTTON_SIZE = { width: 36, height: 36 };

export const ChatComposer: FC<Props> = ({
	value,
	onChange,
	onSend,
	isFirstMessage,
	canAttach,
	onPickPhoto,
	onPickGallery,
	onPickFile,
}) => {
	const theme = useTheme();
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const [attachmentsOpen, setAttachmentsOpen] = useState(false);
	const canSend = value.trim().length > 0;

	const handleSend = () => {
		onSend();
		inputRef.current?.focus();
	};

	return (
		<>
			<Stack
				direction="row"
				alignItems="flex-end"
				gap={theme.spacingTokens.sm}
				flexShrink={0}
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.sm}
				bgcolor={theme.palette.surface.muted}
				borderTop={theme.colors.border.solid(theme.palette.surface.border)}
				pb="max(8px, env(safe-area-inset-bottom, 8px))"
			>
				{canAttach && (
					<IconButton
						variant="ghost"
						onClick={() => setAttachmentsOpen(true)}
						sx={ICON_BUTTON_SIZE}
					>
						<Plus size={theme.iconSize.md} color={theme.palette.blue[500]} />
					</IconButton>
				)}

				<ChatComposerInput
					inputRef={inputRef}
					value={value}
					onChange={onChange}
					onSubmit={onSend}
					isFirstMessage={isFirstMessage}
				/>

				<ChatComposerSendButton onClick={handleSend} active={canSend} />
			</Stack>

			<ChatAttachmentDrawer
				open={attachmentsOpen}
				onClose={() => setAttachmentsOpen(false)}
				onPickPhoto={onPickPhoto}
				onPickGallery={onPickGallery}
				onPickFile={onPickFile}
			/>
		</>
	);
};
